/**
 * Workflows for the Service Desk (M27 S1): the 14 ticket steps (their forms and cards), the ticket facts of the condition editor,
 * and what the Workflows list adds for the Service desk area. Pieces are merged into `engage.workflows` (`steps`, `builder.incomplete`,
 * `conditions`, `extras.problems`) and the rest sits under `engage.workflows.service` and `engage.workflows.defaults.service`.
 * Copy follows mockups/tickets/workflows-service.html.
 */
export const workflowsService = {
  steps: {
    create_ticket: "Create ticket",
    update_ticket: "Update ticket",
    set_ticket_state: "Set ticket status",
    route_ticket: "Route ticket",
    assign_ticket: "Assign ticket",
    add_ticket_note: "Add ticket note",
    reply_to_ticket: "Reply on ticket",
    notify_contact: "Notify client",
    link_ticket: "Link ticket",
    apply_ticket_sla: "Apply ticket SLA",
    request_csat: "Request rating",
    ticket_intake: "Take a ticket request",
    show_ticket_form: "Show ticket form",
    show_my_tickets: "Show my tickets",
    lookup_asset: "Look up asset",
    schedule_visit: "Schedule visit",
    lookup_catalogue: "Look up products",
    check_order_status: "Check order status",
    show_catalogue: "Show products",
    create_order: "Review and confirm order",
    show_order_status: "Show my orders",
  },
  keywords: {
    create_ticket: "ticket, new ticket, open a ticket, raise, incident, request",
    update_ticket: "ticket, change, priority, tags, due date, type",
    set_ticket_state: "ticket, status, resolve, close, pending, move",
    route_ticket: "ticket, routing rules, queue, team, route",
    assign_ticket: "ticket, assign, assignee, team, round robin, skills, give to",
    add_ticket_note: "ticket, internal note, note, mention, comment",
    reply_to_ticket: "ticket, reply, public message, answer the client",
    notify_contact: "client, contact, e-mail, email, chat, messenger, tell the client, notification",
    link_ticket: "ticket, link, attach conversation, connect",
    apply_ticket_sla: "ticket, sla, deadline, service level, response target",
    request_csat: "rating, csat, survey, feedback, stars, ticket",
    ticket_intake: "ticket, intake, concern, assistant, report an issue, request",
    show_ticket_form: "ticket, form, request form, report an issue",
    show_my_tickets: "tickets, my tickets, open requests, status",
    lookup_asset: "asset, equipment, serial number, tag, warranty, find",
    schedule_visit: "visit, engineer, schedule, appointment, field service, windows",
    lookup_catalogue: "catalogue, product, part, search, sku, consumables",
    check_order_status: "order, status, tracking, delivery",
    show_catalogue: "catalogue, products, shop, add to cart, parts",
    create_order: "order, cart, checkout, confirm, buy, place order",
    show_order_status: "orders, my orders, status, tracking",
  },
  incomplete: {
    ticketType: "Choose the ticket type",
    ticketTitle: "Write the ticket title",
    updateNothing: "Choose what to change on the ticket",
    tagsClash: "A tag is both added and removed",
    ticketStatus: "Choose the status to move the ticket to",
    assignTeam: "Choose the team to pick from",
    assignUser: "Choose the person to assign to",
    assignSkills: "Add the skills the person needs",
    noteText: "Write the note",
    replyText: "Write the reply",
    replyAuthor: "Choose which team member the reply comes from",
    notifyAudience: "Choose who to tell",
    notifyChannel: "Choose how to reach them",
    linkVariable: "Name the variable that holds the ticket number",
    ticketPolicy: "Choose the SLA policy",
    assetVariable: "Name the variable that holds the tag or serial number",
    catalogueQuery: "Write what to search for",
    catalogueVariable: "Name the variable that holds the search text",
    orderVariable: "Name the variable that holds the order number",
  },
  problems: {
    no_ticket_here: "This trigger has no ticket for this step. Use a ticket trigger, or create the ticket first.",
    link_needs_conversation: "This trigger has no conversation to link.",
    link_event_no_ticket: "This trigger's event is not about a ticket and a conversation. Link by a variable.",
    service_exits_not_here: "A workflow that talks to the visitor cannot send {{step}} down another path. Remove the exits, or make the workflow run in the background.",
  },
  conditionGroups: {
    ticket: "Ticket",
    organisation: "Organisation",
    contact: "Client contact",
    order: "Order",
    quote: "Quote",
  },
  conditionFields: {
    ticket_number: "Ticket number",
    ticket_type: "Ticket type",
    ticket_category: "Type category",
    ticket_status: "Ticket status",
    ticket_status_category: "Status category",
    ticket_priority: "Priority",
    ticket_channel: "Channel",
    ticket_team_id: "Team",
    ticket_assignee: "Has an assignee",
    ticket_assignee_id: "Assignee",
    ticket_tags: "Tags",
    ticket_organisation_id: "Organisation",
    ticket_org_tier: "Organisation tier",
    ticket_site_region: "Site region",
    ticket_asset_category: "Asset category",
    ticket_has_asset: "Has an asset",
    ticket_contract_active: "Contract is active",
    ticket_warranty_status: "Warranty status",
    ticket_sla_policy: "SLA policy",
    ticket_sla: { first_response_state: "First-response SLA", resolution_state: "Resolution SLA" },
    ticket_reopen_count: "Times reopened",
    ticket_requester_verified: "Requester is verified",
    ticket_language: "Ticket language",
    ticket_origin: "How it was raised",
    ticket_created_in_office_hours: "Created in office hours",
    ticket_sentiment: "How the client sounds",
    organisation_id: "Organisation id",
    organisation_tier: "Tier",
    organisation_industry: "Industry",
    organisation_status: "Status",
    organisation_tags: "Tags",
    contact_org_role: "Role in the organisation",
    contact_can_order: "Can order",
    contact_verified: "Is verified",
    contact_verified_via: "Verified by",
    contact_language: "Language",
    event_from: "Status before",
    event_to: "Status after",
    event_score: "Rating (1–5)",
    event_low: "Rating is at or below the alert level",
    event_outcome: "Outcome",
    event_metric: "SLA metric",
    event_n: "Which reminder (1, 2 ...)",
    event_mentions: "People mentioned",
    order_status: "Order status",
    order_total_php: "Order total (PHP)",
    order_approval_state: "Approval state",
    order_kind: "How the order was made",
    order_connector_id: "Order system",
    order_line_count: "Number of lines",
    order_has_backorder: "Has a backorder line",
    quote_status: "Quote status",
    quote_source: "How the quote was asked for",
    quote_total_php: "Quote total (PHP)",
    quote_line_count: "Number of lines",
    quote_expired: "Quote has expired",
    event_final: "The decision is final",
    event_side: "Who decides (the client or our team)",
    ticket_order_number: "Order number",
    ticket_order_status: "Order status",
    ticket_order_items: "Order items",
  },
  conditionChoices: {
    ticket_priority: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
    ticket_sentiment: { negative: "Upset or angry", neutral: "Neutral", positive: "Happy or grateful" },
    ticket_status_category: { new: "New", open: "Open", pending: "Waiting for the client", on_hold: "On hold", resolved: "Resolved", closed: "Closed" },
    ticket_category: { customer: "Customer request", back_office: "Back office", tracker: "Tracker" },
    ticket_channel: { widget: "Website widget", webapp: "Web app", mobile: "Mobile app", messenger: "Messenger", email: "E-mail", api: "API", console: "Console" },
    ticket_origin: {
      ai_intake: "Assistant intake",
      client_form: "Client form",
      staff: "Raised by staff",
      email: "E-mail",
      api: "API",
      workflow: "A workflow",
      conversation: "Converted from a conversation",
      handoff: "A handoff",
      order_issue: "An order issue",
      visit: "A field visit",
      punch_list: "A punch list",
      pm_plan: "A maintenance plan",
    },
    ticket_sla: {
      first_response_state: { ok: "On track", warning: "Close to the target", breached: "Missed", met: "Met", paused: "Paused", "n/a": "No target" },
      resolution_state: { ok: "On track", warning: "Close to the target", breached: "Missed", met: "Met", paused: "Paused", "n/a": "No target" },
    },
    contact_org_role: { org_admin: "Organisation admin", member: "Member" },
    contact_verified_via: { widget_jwt: "Signed in on the website", email_otp: "E-mail code", magic_link: "E-mail link", staff_verified: "Verified by staff" },
    event_metric: { first_response: "First response", next_response: "Next response", resolution: "Resolution", onsite_response: "On-site response" },
  },
  addStepLock: "The Service Desk is not available in this workspace, so its steps cannot be added.",
  service: {
    notSwitchedOn: "Not switched on",
    priorities: { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" },
    summaries: {
      anyType: "any type",
      create_ticket: "Create a {{type}} ticket: {{title}}",
      create_ticket_any: "Create a ticket: {{title}}",
      updateType: "type {{type}}",
      updatePriority: "priority {{priority}}",
      updateTagsAdd: "add tags {{tags}}",
      updateTagsRemove: "remove tags {{tags}}",
      updateFields_one: "{{count}} answer",
      updateFields_other: "{{count}} answers",
      updateDue_one: "due in {{count}} minute",
      updateDue_other: "due in {{count}} minutes",
      updateClearDue: "clear the due date",
      set_ticket_state: "Move the ticket to {{status}}",
      route_ticket: "Apply the workspace routing rules",
      assign_round_robin: "Round robin in {{team}}",
      assign_least_open: "Whoever has the fewest open tickets in {{team}}",
      assign_skills_match: "By skills in {{team}}: {{skills}}",
      assign_user: "Assign to {{name}}",
      assign_unassign: "Take the ticket off its assignee",
      add_ticket_note: "Note: {{text}}",
      reply_to_ticket: "Reply as {{author}}: {{text}}",
      notify_contact: "Tell {{who}} by {{channels}}: {{template}}",
      link_ticket: "Link the chat to the ticket in {{variable}}",
      apply_ticket_sla: "Apply {{policy}}",
      request_csat: "Ask the client for a rating",
      ticket_intake: "The assistant takes the client's concern",
      ticket_intake_typed: "The assistant takes a {{type}} request",
      show_ticket_form: "Show the {{type}} form",
      show_my_tickets: "Show the client's open requests",
      lookup_asset_event: "Find the asset the event is about",
      lookup_asset_ticket: "Find the asset linked to the ticket",
      lookup_asset_variable: "Find the asset in {{variable}}",
      schedule_visit: "Propose a {{kind}} visit with up to {{windows}} windows",
      lookup_catalogue_query: "Find products for “{{query}}”",
      lookup_catalogue_variable: "Find products for the text in {{variable}}",
      lookup_catalogue_ticket_asset: "Find parts for the ticket's equipment",
      check_order_status_event: "Read the order the event is about",
      check_order_status_variable: "Read the order number in {{variable}}",
      check_order_status_latest: "Read the client's latest order",
      show_catalogue_query: "Show products for “{{query}}”",
      show_catalogue_message: "Show products for what the client asked",
      create_order: "Show the cart for the client to confirm",
      show_order_status: "Show the client's recent orders",
    },
    fields: {
      variableInvalid: "Use a name like var.serial_number: var. followed by letters, digits or underscores.",
      lookupAssetHelp: "Finds an asset of the client's organisation and keeps what it found for the steps that follow.",
      assetSource: "Look for the asset",
      assetSources: { event: "From the event", ticket: "From the ticket", variable: "From a variable" },
      assetSourceHints: {
        event: "The asset the event is about (maintenance and warranty events).",
        ticket: "The asset linked to the run's ticket.",
        variable: "The tag or serial number a variable holds, for example one the client typed.",
      },
      assetVariable: "Variable",
      assetVariableHint: "The run variable that holds the asset tag or serial number.",
      scheduleVisitHelp: "Proposes a visit for the ticket with up to three windows from the engineer's free time. The client always chooses the window.",
      visitKind: "Kind of visit",
      visitKinds: { corrective: "Corrective", pm: "Preventive maintenance", calibration: "Calibration", inspection: "Inspection" },
      visitWindows: "Windows to offer",
      visitWindowsHint: "Up to {{max}}.",
      visitDuration: "Visit length (minutes)",
      visitDurationHint: "Between {{min}} and {{max}} minutes.",
      visitEngineer: "Engineer",
      visitEngineerDefault: "The ticket's assignee, else its team",
      lookupCatalogueHelp: "Searches the catalogue for products this client may see and keeps up to five of them in var.products.",
      catalogueSource: "Search for",
      catalogueSources: { query: "A fixed text", variable: "The text in a variable", ticket_asset: "Parts for the ticket's equipment" },
      catalogueSourceHints: {
        query: "The same words every time.",
        variable: "Words an earlier step or the client gave.",
        ticket_asset: "Products that fit the equipment linked to the ticket.",
      },
      catalogueQuery: "Search text",
      catalogueQueryHint: "Up to 200 characters. You can use variables.",
      catalogueVariable: "Variable",
      catalogueVariableHint: "The run variable that holds the search text.",
      catalogueCategory: "Category (optional)",
      catalogueCategoryHint: "Only products in this category.",
      catalogueLimit: "Products to keep",
      catalogueLimitHint: "Up to {{max}}.",
      checkOrderHelp: "Reads an order of the run's own organisation and keeps its status in var.order_status.",
      orderSource: "Which order",
      orderSources: { event: "The order of the event", variable: "An order number in a variable", latest: "The client's latest order" },
      orderSourceHints: {
        event: "Only the order triggers carry an order.",
        variable: "An order number such as O-00042.",
        latest: "The order the client placed last.",
      },
      orderVariable: "Variable",
      orderVariableHint: "The run variable that holds an order number such as O-00042.",
      showCatalogueHelp: "Shows the products that answer a search as a card. The client can add one to their cart; nothing is ordered.",
      showCatalogueQueryHint: "Leave empty to search for what the client just wrote. You can use variables.",
      catalogueTimeout: "Close the card after",
      catalogueTimeoutHint: "If nobody uses the card it closes and the workflow leaves by Card closed.",
      createOrderHelp: "Shows the client's cart as an order to review, with Confirm order and Cancel.",
      createOrderSafe: "An order is placed only when the client taps Confirm order on the card. Words are never enough, the client's limits and approvers apply, and a large total still asks for an e-mail code. This step cannot run on a background trigger.",
      orderTimeout: "Cancel the review after",
      orderTimeoutHint: "If the client does not decide in time, nothing is ordered and the workflow leaves by Cancelled.",
      showOrderStatusHelp: "Shows the signed-in client's recent orders (five at most) and their statuses. A client who is not signed in is asked to sign in first.",
      choose: "Choose…",
      typeGone: "A type that no longer exists",
      noTypes: "No ticket types to choose from yet.",
      openSettings: "Open Service settings",
      exitsFacing: "This workflow talks to the visitor, so this step runs in their turn and carries on with the next step. Exits are not available here.",
      variables: "Variables you can use",
      ticketType: "Ticket type",
      createTypeHint: "The type decides the form fields, the default team and the SLA of the new ticket.",
      ticketTitle: "Title",
      ticketTitleHint: "Up to 140 characters on the ticket. You can use variables.",
      ticketDescription: "Description",
      priority: "Priority",
      priorityTypeDefault: "The type's default",
      priorityKeep: "Leave as it is",
      team: "Team",
      teamTypeDefault: "The type's default, or the routing rules",
      tags: "Tags",
      tagsHint: "Separate tags with commas.",
      tagsAdd: "Add tags",
      tagsRemove: "Remove tags",
      answers: "Answers to ticket fields",
      answersHint: "Each answer can use variables, such as {{example}}.",
      addAnswer: "Add an answer",
      removeAnswer: "Remove the answer for {{name}}",
      noFields: "Your ticket types have no custom fields yet.",
      notifyClient: "Send the client the receipt e-mail",
      createVariables: "After this step, {{number}} and {{id}} name the new ticket for the steps that follow.",
      updateIntro: "Only what you set here changes; everything else on the ticket stays as it is.",
      typeKeep: "Leave the type as it is",
      due: "Due date",
      dueModes: { keep: "Leave as it is", set: "Set it from now", clear: "Clear it" },
      dueIn: "Due in (minutes)",
      dueInHint: "From the moment the step runs.",
      status: "Move the ticket to",
      statusHint: "Checked against the ticket state machine when the step runs.",
      noStatuses: "No statuses to choose from yet.",
      resolutionCode: "Resolution code",
      noCode: "No code",
      resolutionNote: "Resolution note",
      resolutionNoteHint: "Kept on the ticket with the move. You can use variables.",
      stateMachine: "The Failed exit is a move the state machine refuses (for example, a resolve that needs a code).",
      routeHelp: "Applies the routing rules of the workspace to the ticket, exactly like a new ticket from the console. “Given to a person” means a rule chose someone; “Waiting in the queue” means a team queue got it or no rule matched.",
      openRouting: "Open Service settings",
      assignMethod: "How to choose the person",
      assignMethods: {
        round_robin: "Round robin",
        least_open: "Fewest open tickets",
        skills_match: "By skills",
        user: "A named person",
        unassign: "Nobody (take it off its assignee)",
      },
      assignMethodHints: {
        round_robin: "Each eligible member of the team in turn.",
        least_open: "The eligible team member with the fewest open tickets.",
        skills_match: "A team member who has every skill listed below.",
        user: "Always the same person.",
        unassign: "The ticket stays with its team, with no assignee.",
      },
      teamPick: "Team to pick from",
      teamOptional: "Team (optional)",
      member: "Team member",
      skills: "Required skills",
      skillsHint: "Separate skills with commas. At most {{max}}.",
      noteText: "Note",
      noteHint: "Only your team sees it. You can use variables and @mentions.",
      mentions: "Mention people",
      mentionsHint: "They are notified. At most {{max}}.",
      mentionsPlaceholder: "Nobody mentioned",
      mentionsSelected_one: "{{count}} person mentioned",
      mentionsSelected_other: "{{count}} people mentioned",
      search: "Search people",
      noMatches: "No one matches",
      replyText: "Message to the client",
      replyHint: "Visible to the client on the ticket. You can use variables.",
      replyAuthor: "Written as",
      authors: { system: "The Service Desk", user: "A team member" },
      replyCap: "A run writes at most {{count}} replies to one ticket. After that, this step is skipped.",
      audience: "Who to tell",
      audiences: {
        requester: "The requester",
        participants: "Participants",
        org_admins: "Organisation admins",
        org_approvers: "Order approvers",
        visit_contact: "The visit contact",
      },
      audienceLater: "Approvers and the visit contact are told once Orders and Field visits are switched on. Until then nobody is.",
      channels: "How to reach them",
      channelNames: { email: "E-mail", in_chat: "In the open chat", messenger: "Messenger" },
      channelsHint: "A chat line goes only to someone with a chat open now; Messenger only inside its 24-hour window. If nobody can be reached, the Skipped exit runs.",
      template: "Message",
      templateHint: "The e-mail template to send. The Service Desk wording of each is in Service settings.",
      templates: {
        ticket_received: "We received your request",
        ticket_reply: "A reply from our team",
        ticket_status: "Your request has a new status",
        ticket_waiting_reminder: "We are waiting for your reply",
        ticket_resolved: "Your request was resolved",
      },
      chatLine: "Line for chat and Messenger",
      chatLineHint: "Leave empty to use the standard line of the message. You can use variables.",
      linkSource: "Which ticket",
      linkSourceHint: "The chat is linked to this ticket, so staff see both together.",
      linkSources: { variable: "The ticket named by a variable", event: "The ticket of the event" },
      linkEventLater: "No trigger in this release names both a ticket and a conversation. Link by a variable instead.",
      linkVariable: "Variable",
      linkVariableHint: "For example var.ticket_number, set by Create ticket.",
      linkVariableInvalid: "Use var. followed by letters, numbers or _.",
      slaPolicy: "SLA policy",
      slaPolicyHint: "Replaces the ticket's SLA policy. Its clocks start again from now.",
      policyGone: "A policy that no longer exists",
      slaNone: "No ticket SLA policies yet.",
      csatHelp: "Asks the requester to rate the resolved ticket. The survey rules of the workspace still apply: the fatigue cap, and the types and codes that skip it. “Client was told” means the survey went out.",
      intakeHelp: "The assistant asks the client about the concern one question at a time, checks for an open request about the same thing, shows a summary and creates the ticket only when the client presses Submit.",
      intakeAnyType: "Let the assistant work it out",
      intakeTypeHint: "Choose a type to always raise that one. Otherwise the assistant picks from the conversation and asks when it is not sure.",
      intakeTimeout: "Close the summary after",
      intakeTimeoutHint: "If the client does nothing for this long, the Client left exit runs.",
      formHelp: "A fixed form for one ticket type, with no assistant. The client fills it in and presses Submit.",
      formTypeHint: "The form shows the fields of this type.",
      formTimeout: "Close the form after",
      formTimeoutHint: "If the client does not send it in this time, the Form not sent exit runs.",
      myTicketsHelp: "Shows the verified client's open requests (five at most) with a link to My tickets. A client who is not verified is asked to verify first.",
    },
  },
  list: {
    areas: {
      orders_field_service: "Orders & field service",
    },
    areaNotes: {
      service_desk: "Tickets and clients",
      orders_field_service: "Orders, quotes, visits and assets",
    },
    wdLabel: "Default {{id}}",
    triggerLabel: "Starts on",
    stepsLabel: "Steps",
    kind: { inline: "runs inside the request", async: "runs in the background", facing: "runs in the chat" },
    filters: {
      label: "Filter the default workflows",
      search: "Search by name, trigger or WD number",
      searchLabel: "Search workflows",
      state: "State",
      anyState: "Any state",
      live: "Live",
      paused: "Paused",
      off: "Off by default",
      modified: "Modified",
      kind: "Kind",
      anyKind: "Customer-facing and background",
      facing: "Customer-facing",
      background: "Background",
      area: "Area",
      anyArea: "Every area",
      clear: "Clear filters",
      emptyTitle: "No workflows match",
      emptyBody: "Clear the search or the filters to see every default again.",
      count: "{{shown}} of {{total}} shown",
    },
    module: {
      pausedTitle: "Paused because {{module}} is switched off",
      pausedBody: "It starts again by itself when you switch {{module}} on in Service settings. Nothing was deleted.",
      openSettings: "Open Service settings",
      resumeBlocked: "Resume (switch the module on first)",
      names: { orders: "Orders & catalogue", assets: "Assets", installations: "Projects and installations", visits: "Field visits" },
      badge: "Module off",
    },
    locked: {
      title: "Service desk workflows come with the {{plan}} plan",
      body: "The 18 Service desk defaults tell your clients about their tickets, route new ones and warn before an SLA is missed. They are set up when your plan includes the Service Desk.",
      cta: "Compare plans",
    },
    strong: {
      title: "{{name}} ({{id}}) is paused",
      resume: "Resume",
    },
    pause: {
      highImpact: "This is a high-impact default. Type its name to confirm.",
      typeLabel: "Type the name to confirm",
      typeHint: "Type “{{name}}” exactly as shown.",
    },
  },
} as const;
